import React from 'react';
import Panel from '../../components/ui/Panel';
import ColorPalette from '../../components/ui/ColorPalette';
import CodeBlock from '../../components/ui/CodeBlock';
import { colors } from '../../theme/tokens';

function kebab(name: string): string {
  return name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

const tailwindConfigCode = `// tailwind.config.js
module.exports = {
  theme: {
    extend: {
      colors: {
${Object.entries(colors).map(([name, value]) => `        'osrs-${kebab(name)}': '${value}',`).join('\n')}
      },
    },
  },
}`;

const cssVariablesCode = `/* CSS Variables */
:root {
${Object.entries(colors).map(([name, value]) => `  --osrs-${kebab(name)}: ${value};`).join('\n')}
}`;

const ColorsPage: React.FC = () => {

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Colors</h1>
      <p className="text-osrs-yellow mb-8">
        The OSRS Design System color palette is based on the original game's interface. 
        These colors maintain the nostalgic pixel-art style while providing enough contrast for good readability.
      </p>
      
      <section id="primary-colors" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Primary Colors</h2>
        <p className="text-osrs-yellow mb-6">
          The primary color palette consists of the iconic yellow and gold used throughout the OSRS interface, 
          along with supporting browns and black for contrast.
        </p>
        <ColorPalette category="primary" className="mb-8" />
      </section>
      
      <section id="ui-colors" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">UI Colors</h2>
        <p className="text-osrs-yellow mb-6">
          Stone, highlight, and shadow come from the bank and settings interfaces. Chat stone is the tan fill of the opaque chatbox.
        </p>
        <ColorPalette category="ui" className="mb-8" />
      </section>
      
      <section id="status-colors" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Status Colors</h2>
        <p className="text-osrs-yellow mb-6">
          OSRS uses bright, high-contrast colors to indicate status. Green for positive values (healing, buffs), 
          red for negative values (damage, debuffs), and blue for magical or special items.
        </p>
        <ColorPalette category="status" className="mb-8" />
      </section>
      
      <section id="text-colors" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Text Colors</h2>
        <p className="text-osrs-yellow mb-6">
          Interface text on the bank window samples to #F5E20D. Gold is the docs accent. Cyan is split private chat.
        </p>
        <ColorPalette category="text" className="mb-8" />
      </section>
      
      <section id="implementation" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Implementation</h2>
        
        <Panel title="Tailwind Config" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Add these colors to your Tailwind CSS configuration:
          </p>
          <CodeBlock code={tailwindConfigCode} language="jsx" />
        </Panel>
        
        <Panel title="CSS Variables" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Or use CSS variables:
          </p>
          <CodeBlock code={cssVariablesCode} language="css" />
        </Panel>
      </section>
      
      <section id="usage" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Usage Guidelines</h2>
        
        <Panel id="contrast-accessibility" className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Contrast & Accessibility</h3>
          <p className="text-osrs-yellow mb-2">
            While staying true to OSRS aesthetics, ensure sufficient contrast for text readability:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use yellow or gold text on dark backgrounds</li>
            <li>Use black text on yellow or gold backgrounds</li>
            <li>Consider adding outlines to text for better readability when needed</li>
          </ul>
        </Panel>
        
        <Panel id="status-indications" className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Status Indications</h3>
          <p className="text-osrs-yellow mb-2">
            Follow OSRS conventions for status colors:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Green for healing, buffs, or positive changes</li>
            <li>Red for damage, debuffs, or negative changes</li>
            <li>Blue for magical effects or special items</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default ColorsPage; 